import { useContext, useState } from "react"

import ViewOrEdit from "./ViewOrEdit"

import { Button, Toggle } from "web/components"

import { LocalizationContext } from "web/contexts"

import { useFields, useFormSections, useTopicRecord } from "web/hooks/data"
import { DialogCommands, useDialogModal, useModals } from "web/hooks/modals"

import { humanize } from "web/services/inflector"
import { formatAsShortDate } from "web/services/format"
import routes from "web/routes/constants"

import { InformationCircleIcon } from "@heroicons/react/20/solid"

function refreshObjectLists() {
  document.dispatchEvent(
    new CustomEvent("event-dispatcher:refresh-object-list")
  )
}

function ViewOrEditTopic({
  subjectType,
  linkToSubject,
  subjectId,
  subjectUrl,
  topic,
  recordingId,
  respond,
}) {
  const [fieldFilter, setFieldFilter] = useState("")
  const [showAllFields, setShowAllFields] = useState({ value: false })

  const topicRecord = useTopicRecord({
    id: recordingId,
    subjectType,
    subjectId,
    topicId: topic.id,
  })
  const { user_permissions } = topicRecord
  const { update: canEdit, destroy: canDestroy } = user_permissions
  const hasMultipleSources = topicRecord.data_sources.length > 1

  const deleteButtonCopy = hasMultipleSources ? "Clear Data" : "Delete"
  const deleteDialogTitle = hasMultipleSources ? "Clear Data" : "Delete Record"
  const deleteDialogPrompt = hasMultipleSources
    ? "Account data will be deleted for this record. Are you sure?"
    : "Are you sure you want to delete this?"

  const fields = useFields({ subjectType, topicId: topic.id })
  const sections = useFormSections({ subjectType, topicId: topic.id })
  const confirmDeleteModal = useDialogModal({
    title: deleteDialogTitle,
    prompt: deleteDialogPrompt,
    commands: [DialogCommands.ConfirmDelete, DialogCommands.Cancel],
  })

  async function handleDelete() {
    const dialogResult = await confirmDeleteModal()
    if (dialogResult && dialogResult.response) {
      await topicRecord.destroy({ topic_id: topic.id })
      refreshObjectLists()
      respond()
    }
  }

  async function handleFieldChange(value) {
    await topicRecord.update(value)
    refreshObjectLists()
  }

  function goToSubject() {
    respond()
    if (subjectUrl) {
      window.Turbo.visit(subjectUrl)
    } else if (subjectType === "deal_asset") {
      window.Turbo.visit(
        routes.deal_asset("all", null, topicRecord.deal_id, subjectId)
      )
    } else {
      window.Turbo.visit(routes[subjectType]("all", null, subjectId))
    }
  }

  const createdAt = formatAsShortDate({ value: topicRecord.created_at })
  const hasFooterCommands = canDestroy || linkToSubject

  return (
    <div className="view-or-edit-child-modal">
      <div
        className={`modal-content-above-commands ${hasFooterCommands ? "" : "!h-full"}`}
      >
        {!canDestroy && (
          <div className="mb-3 rounded-md bg-blue-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <InformationCircleIcon
                  className="h-5 w-5 text-blue-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3 flex-1 md:flex md:justify-between">
                <p className="text-sm text-blue-700">
                  This data originates from{" "}
                  {topicRecord.data_sources
                    .map((source) =>
                      source.url ? (
                        <a
                          key={source.id}
                          href={source.url}
                          className="font-medium underline"
                        >
                          {source.name}
                        </a>
                      ) : (
                        <span key={source.id}>{source.name}</span>
                      )
                    )
                    .flatMap((node, index) =>
                      index > 0 ? [", ", node] : [node]
                    )}{" "}
                  it cannot be deleted.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="view-or-edit-tlo-tools flex-wrap gap-y-2">
          <span className="flex grow basis-0 items-center text-sm italic text-neutral-6 sm:grow-0 sm:basis-auto">{`Created by ${topicRecord.creator_name} on ${createdAt}`}</span>
          {canEdit && (
            <div className="view-or-edit-show-all-fields">
              <Toggle
                label="Show All Fields"
                checked={showAllFields.value}
                onClick={() =>
                  setShowAllFields({ value: !showAllFields.value })
                }
              />
            </div>
          )}
          <div className="view-or-edit-quick-filter order-first min-w-full sm:order-none sm:min-w-0">
            <input
              value={fieldFilter}
              placeholder="Search fields..."
              onChange={(e) => setFieldFilter(e.target.value)}
            />
          </div>
        </div>
        <ViewOrEdit
          data={topicRecord}
          fields={fields}
          fieldFilter={fieldFilter}
          sections={sections}
          onFieldChanged={handleFieldChange}
          readOnly={!canEdit}
          showAll={showAllFields.value}
        />
      </div>
      {hasFooterCommands && (
        <div className="modal-commands">
          <div className="left-buttons">
            {canDestroy && (
              <Button variant="secondary_danger" onClick={handleDelete}>
                {deleteButtonCopy}
              </Button>
            )}
          </div>
          <div className="right-buttons">
            {linkToSubject && (
              <Button variant="secondary_default" onClick={goToSubject}>
                {`View ${humanize(subjectType)}`}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function useViewOrEditTopicModal({ subjectType, topic, title }) {
  const { showModal } = useModals()
  const localizationContext = useContext(LocalizationContext)

  return async function ({ recording, isCrossSubject, subjectUrl }) {
    const subjectId = isCrossSubject && recording.subject_id

    return await showModal({
      title: title || humanize(topic.label),
      fullScreen: false,
      widthPixels: 800,
      heightPixels: 600,
      component: ({ respond }) => (
        <LocalizationContext.Provider value={localizationContext}>
          <ViewOrEditTopic
            subjectType={subjectType}
            linkToSubject={isCrossSubject || !!subjectUrl}
            subjectId={subjectId}
            subjectUrl={subjectUrl}
            topic={topic}
            recordingId={recording.id}
            respond={respond}
          />
        </LocalizationContext.Provider>
      ),
    })
  }
}
